import * as React from "react"
import { format } from "date-fns"
import { CalendarIcon, X } from "lucide-react"
import type { DateRange } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"

interface DateRangePickerProps {
    dateRange: DateRange | undefined
    onDateRangeChange: (range: DateRange | undefined) => void
    className?: string
    disabled?: boolean
    /**
     * Allow picking days after today. Off by default: most consumers filter on
     * creation dates, which can never be in the future. Pages that filter on a
     * scheduled time (appointments booked for next week) turn this on, or half
     * the calendar is unclickable.
     */
    allowFuture?: boolean
}

export function DateRangePicker({
    dateRange,
    onDateRangeChange,
    className,
    disabled,
    allowFuture,
}: DateRangePickerProps) {
    const [open, setOpen] = React.useState(false)

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()
        onDateRangeChange(undefined)
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    id="date-range-picker"
                    variant="outline"
                    disabled={disabled}
                    className={cn(
                        "justify-start text-left font-normal h-9",
                        !dateRange && "text-muted-foreground",
                        className
                    )}
                >
                    <CalendarIcon className="h-4 w-4 shrink-0" />
                    {dateRange?.from ? (
                        dateRange.to ? (
                            <span className="truncate">
                                {format(dateRange.from, "MMM d, yyyy")} –{" "}
                                {format(dateRange.to, "MMM d, yyyy")}
                            </span>
                        ) : (
                            <span className="truncate">
                                {format(dateRange.from, "MMM d, yyyy")}
                            </span>
                        )
                    ) : (
                        <span>All Time</span>
                    )}
                    {dateRange?.from && (
                        <div 
                            role="button"
                            tabIndex={0}
                            className="ml-auto flex shrink-0 items-center justify-center rounded-sm opacity-50 hover:opacity-100 p-1"
                            onClick={handleClear}
                            onPointerDown={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                            }}
                        >
                            <X className="h-3.5 w-3.5" />
                        </div>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={(range, selectedDay) => {
                        if (dateRange?.from && dateRange?.to) {
                            onDateRangeChange({ from: selectedDay, to: undefined })
                        } else {
                            onDateRangeChange(range)
                        }
                    }}
                    numberOfMonths={2}
                    showOutsideDays={false}
                    disabled={allowFuture ? undefined : { after: new Date() }}
                />
            </PopoverContent>
        </Popover>
    )
}
